/**
 * Deploying an ad in one action.
 *
 * Deploying used to take two presses (Deploy… → choose a placement → Deploy
 * ad). Now: with exactly one deployed placement the button deploys straight
 * away; with several, one compact menu picks the placement and deploys. A draft
 * also gets **Approve & deploy** — approve, then deploy, stopping with a clear
 * message at whichever step is refused (a banned claim refuses the approval;
 * the kill switch, the emergency pause or an inactive account refuse the
 * deploy). Both steps are the existing routes; nothing is combined
 * server-side, so an ad approved by a refused "Approve & deploy" stays
 * approved and the message says so.
 */

import { useEffect, useId, useRef, useState } from 'react';
import type { AdoptPlacementDto } from '@/lib/api';

/** Placements an ad can be deployed into: those already on the channel (an ad group). */
export function deployablePlacements(placements: AdoptPlacementDto[]): AdoptPlacementDto[] {
  return placements.filter((p) => p.externalAdGroupRef && p.status !== 'removed');
}

function placementLabel(p: AdoptPlacementDto): string {
  const channel = p.channel === 'google_ads' ? 'Google Ads' : p.channel;
  return `${channel} · ${p.id.slice(-6)} (${p.status})`;
}

/**
 * The deploy button: one click with one placement, a menu with several, a
 * disabled button (and why) with none.
 */
export function DeployButton({
  label,
  placements,
  preferredPlacementId,
  onDeploy,
  pending,
  primary = true,
}: {
  label: string;
  placements: AdoptPlacementDto[];
  /** The creative's own placement, listed first when it can take the ad. */
  preferredPlacementId: string | null;
  onDeploy: (placementId: string) => void;
  pending: boolean;
  primary?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const deployable = deployablePlacements(placements).sort((a, b) =>
    a.id === preferredPlacementId ? -1 : b.id === preferredPlacementId ? 1 : 0,
  );

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent): void => {
      if (
        e instanceof KeyboardEvent
          ? e.key === 'Escape'
          : !wrapperRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);

  const buttonClass = primary
    ? 'px-2.5 py-1 rounded-md bg-primary text-primary-foreground text-xs disabled:opacity-50'
    : 'px-2.5 py-1 rounded-md border border-border text-xs disabled:opacity-50';

  if (deployable.length === 0) {
    return (
      <button
        type="button"
        disabled
        title="No placement is on Google yet — deploy a placement first (Placements tab)."
        className={buttonClass}
      >
        {label}
      </button>
    );
  }

  if (deployable.length === 1) {
    return (
      <button
        type="button"
        onClick={() => onDeploy(deployable[0].id)}
        disabled={pending}
        title={`Deploys into ${placementLabel(deployable[0])}`}
        className={buttonClass}
      >
        {pending ? 'Working…' : label}
      </button>
    );
  }

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={pending}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        className={buttonClass}
      >
        {pending ? 'Working…' : `${label} ▾`}
      </button>
      {open && (
        <ul
          id={menuId}
          role="menu"
          aria-label={`${label} into`}
          className="absolute right-0 z-10 mt-1 min-w-[14rem] rounded-md border border-border bg-background p-1 shadow-md"
        >
          {deployable.map((p) => (
            <li key={p.id} role="none">
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  onDeploy(p.id);
                }}
                className="w-full rounded px-2 py-1.5 text-left text-xs hover:bg-muted"
              >
                {placementLabel(p)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Whether a deploy-capable control should show: no placement at all hides nothing — it explains. */
export function noDeployablePlacementNote(placements: AdoptPlacementDto[]): string | null {
  return deployablePlacements(placements).length === 0
    ? 'No placement is on Google yet — deploy a placement first (Placements tab).'
    : null;
}
